"use client";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import DealerCard from "./DealerCard";
import { getDealers, getModels, getNearbyDealerCities } from "@/app/utilities/apis";
import { SIDEBAR_DEALER_LIMIT } from "@/app/utilities/constants";

interface DealerSidebarProps {
    title?: string;
    subtitle?: string;
    showViewAll?: boolean;
    prefetchedCity?: string;
    prefetchedCitySlug?: string;
    brandSlug?: string;
    brandName?: string;
    lat?: number;
    lng?: number;
}


export type DealersType = {
    id: string;
    name: string;
    address: string;
};

const DealerSidebar = ({
    title = "",
    subtitle = "Authorised dealers near you",
    showViewAll = true,
    prefetchedCity = "",
    prefetchedCitySlug = "",
    brandSlug = "",
    brandName = "",
    lat,
    lng,
}: DealerSidebarProps) => {
    const [dealersToDisplay, setDealersToDisplay] = useState<DealersType[]>([]);
    const [allModels, setAllModels] = useState<any[]>([]);

    const mapDealerRow = (dealer: any): DealersType => ({
        id: dealer._id,
        name: dealer.display_name,
        address: dealer.address,
    });

    const fetchNearbyCitySlugs = async (cityLat: number, cityLng: number): Promise<string[]> => {
        const response = await getNearbyDealerCities({
            lat: cityLat,
            lng: cityLng,
            slug: prefetchedCitySlug,
            brand_slug: brandSlug,
            limit: 6,
            maxDistance: 24000,
        });
        const cities = Array.isArray(response.data) ? response.data : [];
        return cities
            .map((city: { slug?: string }) => city.slug)
            .filter((slug: string | undefined): slug is string => Boolean(slug));
    };

    const fetchDealers = useCallback(async () => {
        const collected: DealersType[] = [];
        const seenIds = new Set<string>();

        const appendFromCity = async (citySlug: string) => {
            if (collected.length >= SIDEBAR_DEALER_LIMIT) return;
            const response = await getDealers({
                city_slug: citySlug,
                brand_slug: brandSlug,
                limit: SIDEBAR_DEALER_LIMIT,
                sort: "name_asc",
            });
            for (const dealer of response?.data ?? []) {
                if (!dealer?._id || seenIds.has(dealer._id)) continue;
                seenIds.add(dealer._id);
                collected.push(mapDealerRow(dealer));
                if (collected.length >= SIDEBAR_DEALER_LIMIT) break;
            }
        };

        await appendFromCity(prefetchedCitySlug);

        if (collected.length < SIDEBAR_DEALER_LIMIT && lat !== undefined && lng !== undefined) {
            const nearbySlugs = await fetchNearbyCitySlugs(lat, lng);
            for (const slug of nearbySlugs) {
                if (slug === prefetchedCitySlug) continue;
                await appendFromCity(slug);
                if (collected.length >= SIDEBAR_DEALER_LIMIT) break;
            }
        }

        setDealersToDisplay(collected);
    }, [prefetchedCitySlug, brandSlug, lat, lng]);

    const fetchAllModels = async () => {
        const response = await getModels({
            brand: brandSlug,
            limit: -1,
            is_upcoming: false,
            is_discontinued: false,
        });

        const models = response?.data ?? [];
        setAllModels(
            models?.map((model: any) => ({
                _id: model._id,
                name: model.name,
                slug: model.slug,
                brand_name: model.brand_name,
                brand_slug: model.brand_slug,
            })),
        );
    };

    useEffect(() => {
        if (prefetchedCitySlug && brandSlug) {
            fetchDealers();
            fetchAllModels();
        }
    }, [prefetchedCitySlug, brandSlug, fetchDealers]);

    if (dealersToDisplay.length === 0) return null;

    return (
        <div className="rounded-[24px] border border-[#EAECF0] bg-white p-[16px]">
            {/* Header */}
            <div className="flex justify-between items-start mb-1">
                <h2 className="text-[22px] font-bold text-[#101828] leading-tight">{title}</h2>
                <Link
                    href={`/${brandSlug}/dealer-showrooms/${prefetchedCitySlug}`}
                    title={`${brandName} dealers in ${prefetchedCity}`}
                    className="flex items-center gap-1 rounded-[8px] border border-[#D8DEE8] bg-[#F8FAFC] px-3 py-1.5 text-[13px] font-medium text-[#344054] hover:bg-gray-50 transition-colors"
                >
                    {prefetchedCity}
                    <Image
                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}right-arrow-icon.svg`}
                        alt="view-all"
                        title="view-all"
                        width={16}
                        height={16}
                        className="w-3 h-3"
                    />
                </Link>
            </div>
            <p className="text-[14px] text-[#667085] mb-6">{subtitle}</p>

            {/* Dealer Cards Container */}
            <div className="flex flex-col mb-4">
                {dealersToDisplay.map((dealer) => (
                    <DealerCard key={dealer.id} dealer={dealer} allModels={allModels} />
                ))}
            </div>

            {/* Footer Button (Conditional based on showViewAll prop) */}
            {showViewAll && (
                <Link
                    href={`/${brandSlug}/dealer-showrooms/${prefetchedCitySlug}`}
                    className="flex w-full items-center justify-center gap-2 rounded-[16px] border border-[#EAECF0] bg-[#FFFFFF] py-[12px] text-center text-[16px] font-bold text-[#343941] hover:bg-gray-50 transition-all active:scale-[0.98]"
                >
                    View All {brandName} Dealers
                    <Image
                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
                        alt="view-all"
                        width={20}
                        height={20}
                        className="invert"
                    />
                </Link>
            )}
        </div>
    );
};

export default DealerSidebar;
